import type { AgentMessage } from "@/hooks/use-stream-conversation";
import { type PlainAgentExecution } from "@/queries/agent-queries";
import { AgentExecution_Phase } from "gitpod-next-api/gitpod/v1/agent_pb";

/**
 * Simplified representation of the state of a conversation.
 * This can be computed from the desired and actual phase.
 */
export const enum AgentExecutionState {
    PENDING = "Pending",
    WORKING = "Working",
    WAITING_FOR_INPUT = "Waiting for input",
    FAILED = "Failed",
    READY_FOR_REVIEW = "Ready for review",
    STOPPING = "Stopping",
    STOPPED_BY_USER = "Stopped",
}

export const agentExecutionState = (agentExecution: PlainAgentExecution): AgentExecutionState | null => {
    if (!agentExecution.status?.phase) {
        return null;
    }

    // If the desired phase is stopped it means the user has stopped the conversation
    if (agentExecution?.spec?.desiredPhase === AgentExecution_Phase.STOPPED) {
        if (
            agentExecution.status?.phase === AgentExecution_Phase.WAITING_FOR_INPUT ||
            agentExecution.status?.phase === AgentExecution_Phase.STOPPED
        ) {
            return AgentExecutionState.STOPPED_BY_USER;
        }
        return AgentExecutionState.STOPPING;
    }

    switch (agentExecution.status.phase) {
        case AgentExecution_Phase.PENDING:
            return AgentExecutionState.PENDING;
        case AgentExecution_Phase.RUNNING:
            return AgentExecutionState.WORKING;
        case AgentExecution_Phase.WAITING_FOR_INPUT:
            return AgentExecutionState.WAITING_FOR_INPUT;
        case AgentExecution_Phase.STOPPED:
            return agentExecution.status.failureMessage
                ? AgentExecutionState.FAILED
                : AgentExecutionState.READY_FOR_REVIEW;
        default:
            return null;
    }
};

export function canStopAgentExecution(agentExecution: PlainAgentExecution) {
    // If it's already stopped you can't stop it again
    if (agentExecution.spec?.desiredPhase === AgentExecution_Phase.STOPPED) {
        return false;
    }

    switch (agentExecution.status?.phase) {
        case AgentExecution_Phase.PENDING:
        case AgentExecution_Phase.UNSPECIFIED:
        case AgentExecution_Phase.RUNNING:
            return true;
        default:
            return false;
    }
}

export const agentExecutionName = (agentExecution: PlainAgentExecution) =>
    agentExecution.metadata?.name || "New Session";

export const messageToText = (message: AgentMessage) => {
    return message.fragments
        .map((fragment) => {
            switch (fragment.type) {
                case "text":
                    return fragment.content;
                case "action":
                    return `Action: ${fragment.intentDescription}`;
                case "fileModification":
                    return `File modification: ${fragment.filePath}`;
                case "environmentCreation":
                    return `Environment created: ${fragment.environmentId}`;
                case "thinking":
                    return `Thinking: ${fragment.content}`;
            }
        })
        .join("\n\n");
};

export const agentExecutionEnvironmentIds = (agentExecution?: PlainAgentExecution) => {
    if (!agentExecution) {
        return [];
    }

    // Get all environment IDs associated with this execution
    const environmentIds = new Set<string>();

    // From code context
    if (
        agentExecution.spec?.codeContext?.context?.case === "environmentId" &&
        agentExecution.spec?.codeContext?.context?.value
    ) {
        environmentIds.add(agentExecution.spec.codeContext.context.value);
    }

    // From metadata
    if (agentExecution.status?.usedEnvironments) {
        agentExecution.status.usedEnvironments.forEach((envUsage) => {
            if (envUsage.environmentId) {
                environmentIds.add(envUsage.environmentId);
            }
        });
    }

    return Array.from(environmentIds);
};

export const getActiveEnvironmentId = (agentExecution?: PlainAgentExecution) => {
    if (!agentExecution) {
        return undefined;
    }

    // Code context takes precedence
    if (
        agentExecution.spec?.codeContext?.context?.case === "environmentId" &&
        agentExecution.spec?.codeContext?.context?.value
    ) {
        return agentExecution.spec.codeContext.context.value;
    }

    // If not we fall back to environment use - where the most recent is considered the active one
    const usedEnvironmentIds = agentExecution.status?.usedEnvironments ?? [];
    if (usedEnvironmentIds.length > 0) {
        return usedEnvironmentIds[usedEnvironmentIds.length - 1].environmentId;
    }

    return undefined;
};

export const agentExecutionLoading = (agentExecution?: PlainAgentExecution) => {
    if (!agentExecution) {
        return false;
    }

    if (agentExecution.spec?.desiredPhase !== AgentExecution_Phase.RUNNING) {
        return false;
    }

    if (
        agentExecution.status?.phase === AgentExecution_Phase.RUNNING ||
        agentExecution.status?.phase === AgentExecution_Phase.PENDING
    ) {
        return true;
    }

    return false;
};

export const agentExecutionWaitingForInput = (agentExecution?: PlainAgentExecution) => {
    if (!agentExecution) {
        return false;
    }

    const state = agentExecutionState(agentExecution);

    if (state === AgentExecutionState.WAITING_FOR_INPUT) {
        return true;
    }

    // READY_FOR_REVIEW means status.phase=STOPPED without failure. This is
    // genuinely "waiting for input" only when the desired phase is not RUNNING.
    // When desiredPhase=RUNNING but status hasn't caught up yet, the engine is
    // restarting (e.g. after a follow-up message) — not waiting for the user.
    if (state === AgentExecutionState.READY_FOR_REVIEW) {
        return agentExecution.spec?.desiredPhase !== AgentExecution_Phase.RUNNING;
    }

    return false;
};

export const agentExecutionNeedsAttention = (agentExecution?: PlainAgentExecution) => {
    if (!agentExecution) {
        return false;
    }

    if (
        agentExecution.status?.phase === AgentExecution_Phase.WAITING_FOR_INPUT ||
        agentExecution.status?.phase === AgentExecution_Phase.STOPPED
    ) {
        return true;
    }

    return false;
};

export const mostRecentAgentExecution = (agentExecutions: PlainAgentExecution[]): PlainAgentExecution | undefined => {
    return agentExecutions.toSorted((a, b) => {
        return Number((b.metadata?.createdAt?.seconds ?? BigInt(0)) - (a.metadata?.createdAt?.seconds ?? BigInt(0)));
    })[0];
};
